import { Contact } from "./contact";

export type RoomType = "U" | "D" | "P" | "G" | "C";

export interface ChatItem {
  AvatarUrl: string | null;
  Created: string;
  CreatedLong: number;
  Creator: string;
  Description: string | null;
  HostId: string;
  ID: string;
  LastMessage: string;
  Modified: string;
  ModifiedLong: number;
  Modifier: string;
  PID: string;
  Pin: boolean;
  RoomName: string;
  ThreadId: string;
  Type: RoomType;
  IsMuted: boolean;
}

export interface CreateChatRoomBody {
  pID: string;
  roomName: string;
  roomType?: RoomType;
  avatarUrl?: string;
}

export interface RoomInfo {
  AvatarUrl: string;
  CUI: string;
  CUIPartner: string | null;
  Endpoint: string;
  ID: string;
  RoomId: string;
  RoomName: string;
  RoomType: RoomType;
  ThreadId: string;
  Token: string;
  TokenUtcExp: string;
  Members: Contact[];
}

export interface AddMemberInRoomBody {
  roomId: string;
  participants: string[];
}

export interface RemoveMemberInRoomBody {
  roomId: string;
  participants: string[];
}

export interface UpdateChatRoomBody {
  roomId: string;
  newName?: string;
  avatarUrl?: string;
  roomType?: RoomType;
}

export interface SetRoomAdminBody {
  roomId: string;
  userId: string; // Backend expects 'userId', not 'participantId'
  admin: boolean; // Backend expects 'admin', not 'isAdmin'
}

export interface PinnedMessage {
  MessageId: string;
  Creator: string;
  Message: string;
  Type: "Text" | "File" | "Image" | "Video";
  AttachmentType?: "Image" | "Document" | "Video";
  ThumbUrl?: string;
  CreatedOn: string;
}

// Reaction types
export interface MessageReaction {
  MessageId: string;
  TotalReactions: number;
  MyReaction: number | null; // 0 for no reaction, 1-7 for reaction type
  OtherReaction: number[]; // Array of reactions from other users
}

export interface GetMessageReaction {
  Id: string;
  Creator: string;
  Reaction: number; // 0-7
  Created: string;
  AvatarUrl: string;
  ContactName?: string;
}

export interface AddMessageReactionBody {
  MessageId: string;
  roomId: string;
  Reaction: number; // 0-7 to add/change, -1 to remove
}

export interface GetMessageReactionBody {
  messageId: string;
  pageIndex: number;
  pageSize: number;
}

export interface GetReactionsInRoomBody {
  roomId: string;
  pageIndex: number;
  pageSize: number;
}

export interface CheckRoomAdminBody {
  roomId: string;
  userId: string;
}

export interface GetResourceInRoomBody {
  roomId: string;
  type: "File" | "Link" | "Media";
  keyword?: string;
  createdBy?: string;
  fromDate?: string;
  toDate?: string;
  pageIndex: number;
  pageSize: number;
}

export interface GetMediaResourceInRoomResponse {
  CreatedDate: string;
  Creator: string;
  Id: string;
  MediaType: "Image" | "Video";
  MediaUrl: string;
  Message: string;
  MessageId: string;
  ThumbUrl: string;
  Type: string;
}

export interface GetFileResourceInRoomResponse {
  AttachmentType: string;
  AttachmentUrl: string;
  CreatedDate: string;
  Creator: string;
  Id: string;
  Message: string;
  MessageId: string;
  ThumbUrl: string;
  Type: string;
}

export interface GetLinkResourceInRoomResponse {
  CreatedDate: string;
  Creator: string;
  Id: string;
  LinkDescription: string;
  LinkImage: string;
  LinkSource: string;
  LinkTitle: string;
  LinkUrl: string;
  Message: string;
  MessageId: string;
  Type: string;
}

export type GetResourceInRoomResponse =
  | GetMediaResourceInRoomResponse[]
  | GetFileResourceInRoomResponse[]
  | GetLinkResourceInRoomResponse[];

// Message types

export interface CommunicationUserIdentifier {
  communicationUser: {
    id: string;
  };
}

export interface ChatMessageSender {
  communicationUserId: string;
  kind: string;
}

// Metadata interfaces
export interface BaseMetadata {
  [key: string]: string | undefined;
}

export interface ImageMetadata extends BaseMetadata {
  width?: string;
  height?: string;
  url?: string;
  attachmentUrl?: string;
  thumbnailUrl?: string;
  name?: string;
  size?: string;
  // ACS specific
  attachmentId?: string;
  // Message confirmation status
  needConfirmed?: string;
}

export interface VideoMetadata extends BaseMetadata {
  width?: string;
  height?: string;
  url?: string;
  duration?: string;
  name?: string;
  // Message confirmation status
  needConfirmed?: string;
}

export interface FileMetadata extends BaseMetadata {
  name?: string;
  size?: string;
  url?: string;
  extension?: string;
  // Message confirmation status
  needConfirmed?: string;
}

export interface LinkPreviewMetadata extends BaseMetadata {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  // Message confirmation status
  needConfirmed?: string;
}

export interface LocationMetadata extends BaseMetadata {
  latitude?: string;
  longitude?: string;
  address?: string;
  name?: string;
  // Message confirmation status
  needConfirmed?: string;
}

export interface AlbumMetadata extends BaseMetadata {
  images?: string; // JSON string
  urls?: string; // Semicolon-separated URLs
  thumbs?: string; // Semicolon-separated thumbnail URLs
  // Message confirmation status
  needConfirmed?: string;
}

export interface ReplyMetadata extends BaseMetadata {
  replyMessageId?: string;
  replyMessage?: string;
  replySenderName?: string;
  replyUrl?: string;
}

export type MessageContentType =
  | "text"
  | "html"
  | "topicUpdated"
  | "participantAdded"
  | "participantRemoved"
  | "delete";

export interface ChatMessageContent {
  message?: string;
  topic?: string;
  participants?: CommunicationUserIdentifier[];
  attachments?: Array<{ id: string; attachmentType: string; name?: string; url?: string }>;
}

export interface AppChatMessage {
  id: string;
  type: MessageContentType;
  sequenceId: string;
  version: string;
  content?: ChatMessageContent;
  senderDisplayName?: string;
  createdOn: Date | string;
  editedOn?: Date | string;
  deletedOn?: Date | string;
  senderCommunicationIdentifier?: CommunicationUserIdentifier;
  metadata?: Record<string, string>;
  // Legacy/Fallback supports
  sender?: ChatMessageSender;
  isDeleted?: boolean; // Custom field for soft delete handling
}

// For backward compatibility
export type ChatMessage = AppChatMessage;

// Specialized Message Types for Components
export interface TextMessage extends AppChatMessage {
  content: {
    message: string;
  };
}

// Type Guards & Utils

export const isTextMessage = (message: AppChatMessage): message is TextMessage => {
  return message.type === "text" || message.type === "html";
};

export const isParticipantAddedMessage = (message: AppChatMessage): boolean => {
  return message.type === "participantAdded";
};

export const isParticipantRemovedMessage = (message: AppChatMessage): boolean => {
  return message.type === "participantRemoved";
};

export const isTopicUpdatedMessage = (message: AppChatMessage): boolean => {
  return message.type === "topicUpdated";
};

export const isDeletedMessage = (message: AppChatMessage): boolean => {
  return !!message.deletedOn || message.type === "delete" || !!message.isDeleted;
};

// Helper xác định loại nội dung dựa trên metadata (vì ACS gửi mọi thứ là text/html)
export type AppContentType = "text" | "image" | "video" | "file" | "location" | "link" | "album" | "system";

export const getMessageContentType = (message: AppChatMessage): AppContentType => {
  if (isDeletedMessage(message)) return "text"; // Render as text "Tin nhắn đã thu hồi"

  if (message.type === "participantAdded" || message.type === "participantRemoved" || message.type === "topicUpdated") {
    return "system";
  }

  const metadata = message.metadata || {};

  // Check for attachments using hasAttachment field (old module style)
  if (metadata.hasAttachment === "true") {
    // Check if it's an album (has type: "album" or urls field with semicolons)
    if (
      metadata.type === "album" ||
      (metadata.urls && typeof metadata.urls === "string" && metadata.urls.includes(";"))
    ) {
      return "album";
    }

    // Determine type based on mimeType
    const mimeType = metadata.mimeType || "";
    if (mimeType.startsWith("image/")) {
      return "image";
    }
    if (mimeType.startsWith("video/")) {
      return "video";
    }
    // Default to file for other attachments
    return "file";
  }

  // Fallback to old detection methods for backward compatibility
  if (metadata.hasImage === "true" || (metadata.type === "image" && !metadata.isAlbum)) return "image";
  if (metadata.hasVideo === "true" || metadata.type === "video") return "video";
  if (metadata.hasFile === "true" || metadata.type === "file") return "file";
  if (metadata.hasLocation === "true" || metadata.type === "location") return "location";
  if (metadata.isLink === "true" || metadata.type === "link" || metadata.type === "linkPreview") return "link";
  if (metadata.isAlbum === "true" || metadata.type === "album") return "album";

  return "text";
};

// Azure SDK Types
export type { ChatThreadClient } from "@azure/communication-chat";

export interface MessageIterator {
  next(): Promise<{ value: AppChatMessage[]; done: boolean }>;
}

// Metadata type for messages
export type MessageMetadata =
  | ImageMetadata
  | VideoMetadata
  | FileMetadata
  | LinkPreviewMetadata
  | LocationMetadata
  | AlbumMetadata
  | ReplyMetadata
  | Record<string, string>;

// API Error type
export interface ApiError {
  response?: {
    data?: unknown;
    status?: number;
    statusText?: string;
  };
  message: string;
  code?: string;
}

// Search types
export interface SearchMessage {
  AvatarUrl: string;
  CreatedDate: string;
  Creator: string;
  Id: string;
  Message: string;
  MessageId: string;
  Type: string;
}

export interface ParamsSearchMessage {
  roomId: string;
  keyword: string;
  pageIndex?: number;
  pageSize?: number;
}

export interface PinRoomBody {
  roomId: string;
  pin: boolean;
}

export interface TransferOwnerBody {
  RoomId: string;
  ToUserId: string;
}

export interface MessageConfirmationBody {
  messageId: string;
  threadId: string;
}

export interface MemberConfirmation {
  UserId: string;
  ContactName: string;
  Description: string | null;
  Unit: string | null;
  AvatarUrl: string;
  AckTime: string | null;
  AckTimeLong: string | null;
  Created: string | null;
  CreatedLong: string | null;
  Creator: string | null;
}

export interface GetMemberConfirmationPayload {
  messageId: string;
  roomId: string;
  ack?: boolean; // truyền true là danh sách member đã xác nhận, truyền false là danh sách member chưa xác nhận không truyền sẽ lấy tất cả
}

export interface GetReaderItem {
  Id: string;
  UserId: string;
  ContactName: string;
  Description: string | null;
  AvatarUrl: string;
  Seen: string;
  SeenLong: number;
  Created: string;
  CreatedLong: number;
  Creator: string;
  Read: boolean;
}

export interface renderSchema {
  roomId: string; // là id của room chat
  seen?: number; // thời gian muốn kiểm tra người xem đã mở room và đọc tin, chuyển datetime sang kiểu số
  read?: boolean | null; // nếu là null thì lấy tất cả, nếu là false thì lọc chưa đọc, true lọc đã đọc
}
